import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Upload,
  Sparkle,
  CheckCircle,
  Warning,
  ArrowRight,
  Spinner,
  FileXls,
  Table as TableIcon,
  X,
} from "@phosphor-icons/react";
import { API } from "@/contexts/AuthContext";

export default function ImportPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState(null);
  const [analysis, setAnalysis] = useState(null); // { sheets, field_targets, application_targets }
  const [activeSheetIdx, setActiveSheetIdx] = useState(0);
  const [overrides, setOverrides] = useState({}); // { sheetIdx: { source_header: target_field_or_null } }
  const [commitResult, setCommitResult] = useState(null);

  useEffect(() => {
    document.title = "Plantus · Excel-Import";
  }, []);

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Datei zu groß (max. 8 MB).");
      return;
    }
    setFile(f);
  };

  const startAnalyze = async () => {
    if (!file) return;
    setBusy(true);
    setAnalysis(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/app/import/analyze`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      // overrides initialisieren mit den KI-Vorschlaegen
      const init = {};
      data.sheets.forEach((sh, idx) => {
        init[idx] = {};
        sh.mappings.forEach((m) => (init[idx][m.source] = m.target));
      });
      setOverrides(init);
      setAnalysis(data);
      setStep(2);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Analyse fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const activeSheet = analysis?.sheets?.[activeSheetIdx];
  const targets =
    activeSheet?.data_type === "applications"
      ? analysis.application_targets
      : analysis?.field_targets || {};

  const updateMapping = (source, target) => {
    setOverrides((o) => ({...o,
      [activeSheetIdx]: {...(o[activeSheetIdx] || {}), [source]: target || null },
    }));
  };

  const updateDataType = (newType) => {
    setAnalysis((a) => {
      const sheets = a.sheets.slice();
      sheets[activeSheetIdx] = {...sheets[activeSheetIdx], data_type: newType };
      return {...a, sheets };
    });
  };

  const startCommit = async () => {
    if (!activeSheet) return;
    setBusy(true);
    setCommitResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("data_type", activeSheet.data_type);
      fd.append("sheet_name", activeSheet.sheet_name);
      fd.append("mapping", JSON.stringify(overrides[activeSheetIdx] || {}));
      const { data: result } = await axios.post(`${API}/app/import/commit-file`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setCommitResult(result);
      setStep(3);
    } catch (err) {
      const d = err.response?.data?.detail;
      if (err.response?.status === 402) {
        const detail = typeof d === "object" ? d : {};
        toast.error(detail.message || "Free-Limit erreicht.", {
          action: { label: "Upgrade", onClick: () => navigate("/preise") },
          duration: 10000,
        });
      } else {
        toast.error(typeof d === "string" ? d : "Import fehlgeschlagen.");
      }
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setStep(1);
    setFile(null);
    setAnalysis(null);
    setOverrides({});
    setCommitResult(null);
    setActiveSheetIdx(0);
  };

  return (
    <div className="max-w-5xl space-y-8" data-testid="import-page">
      <header>
        <h1 className="font-heading text-3xl font-semibold text-forest-900">Excel & CSV importieren</h1>
        <p className="font-body text-[15px] text-[#4A524C] mt-1 max-w-2xl">
          Lade deine Excel- oder CSV-Liste hoch. Plantus erkennt automatisch was darin steht und mappt die Spalten auf die richtigen Felder.
          Du prüfst die Zuordnung und bestätigst mit einem Klick.
        </p>
      </header>

      <Stepper step={step} />

      {step === 1 && (
        <section className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-10" data-testid="import-step-upload">
          <div className="text-center max-w-md mx-auto">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-forest-50 text-forest-700 flex items-center justify-center mb-4">
              <Upload size={28} weight="duotone" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-forest-900">Datei auswählen</h2>
            <p className="text-sm text-[#4A524C] mt-1">
              Unterstützt werden <strong>.xlsx,.xls,.csv</strong> bis 8 MB.
            </p>

            <label
              className="mt-6 block cursor-pointer rounded-2xl border-2 border-dashed border-forest-200 bg-bone/40 hover:bg-bone/70 hover:border-forest-300 transition px-6 py-10"
              data-testid="import-dropzone"
            >
              <input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={onPick}
                className="hidden"
                data-testid="import-file-input"
              />
              {file ? (
                <div className="flex items-center justify-center gap-3 text-forest-900">
                  <FileXls size={24} weight="duotone" />
                  <span className="font-medium">{file.name}</span>
                  <span className="text-xs text-[#7A827C]">({Math.round(file.size / 1024)} KB)</span>
                </div>
              ) : (
                <div className="text-sm text-[#4A524C]">
                  Klicke hier oder ziehe deine Datei hierhin
                </div>
              )}
            </label>

            <button
              onClick={startAnalyze}
              disabled={!file || busy}
              data-testid="import-analyze-btn"
              className="mt-6 inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/40 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
            >
              {busy ? (
                <>
                  <Spinner size={16} className="animate-spin" /> Plantus analysiert deine Datei…
                </>
              ) : (
                <>
                  <Sparkle size={16} weight="fill" /> KI-Analyse starten
                </>
              )}
            </button>
          </div>
        </section>
      )}

      {step === 2 && activeSheet && (
        <section className="space-y-5" data-testid="import-step-mapping">
          {/* Sheet-Tabs falls mehrere Sheets */}
          {analysis.sheets.length > 1 && (
            <div className="flex gap-2 flex-wrap">
              {analysis.sheets.map((s, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveSheetIdx(idx)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    activeSheetIdx === idx
                      ? "bg-forest-900 text-white"
                      : "bg-white border border-bone-200 text-forest-700 hover:bg-bone"
                  }`}
                >
                  {s.sheet_name} · {s.total_rows} Zeilen
                </button>
              ))}
            </div>
          )}

          <div className="bg-white rounded-3xl border border-bone-200 p-6">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-forest-700">
                  <Sparkle size={14} weight="fill" /> KI-Vorschlag (Konfidenz {Math.round((activeSheet.confidence || 0) * 100)} %)
                </div>
                <h2 className="font-heading text-xl font-semibold text-forest-900 mt-1">
                  Erkannter Daten-Typ:
                  <span className="ml-2 inline-flex items-center gap-1 text-sm font-medium bg-forest-50 text-forest-700 px-2.5 py-1 rounded-full">
                    {activeSheet.data_type === "fields"
                      ? "Flächen / Schläge"
                      : activeSheet.data_type === "applications"
                      ? "Anwendungen / Spritztagebuch"
                      : "Unbekannt"}
                  </span>
                </h2>
                {activeSheet.suggestion && (
                  <p className="text-sm text-[#4A524C] mt-2 max-w-2xl">{activeSheet.suggestion}</p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <span className="text-xs text-[#7A827C]">Daten-Typ:</span>
                <select
                  value={activeSheet.data_type}
                  onChange={(e) => updateDataType(e.target.value)}
                  className="rounded-full border border-bone-200 bg-white px-3 py-1.5 text-sm"
                  data-testid="import-datatype-select"
                >
                  <option value="fields">Flächen</option>
                  <option value="applications">Anwendungen</option>
                </select>
              </div>
            </div>

            {/* Mapping-Tabelle */}
            <div className="mt-6 overflow-x-auto" data-testid="import-mapping-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs uppercase tracking-[0.16em] text-[#7A827C] border-b border-bone-200">
                    <th className="text-left py-2 pr-4">Spalte aus deiner Datei</th>
                    <th className="text-left py-2 pr-4">Plantus-Feld</th>
                    <th className="text-left py-2 pr-4">Konfidenz</th>
                  </tr>
                </thead>
                <tbody>
                  {activeSheet.mappings.map((m, i) => {
                    const current = overrides[activeSheetIdx]?.[m.source] ?? null;
                    const conf = Math.round((m.confidence || 0) * 100);
                    return (
                      <tr key={i} className="border-b border-bone-100">
                        <td className="py-3 pr-4 font-medium text-forest-900">{m.source}</td>
                        <td className="py-3 pr-4">
                          <select
                            value={current || ""}
                            onChange={(e) => updateMapping(m.source, e.target.value || null)}
                            className="rounded-lg border border-bone-200 bg-white px-2 py-1.5 text-sm w-full max-w-xs"
                            data-testid={`mapping-${i}`}
                          >
                            <option value="">, nicht importieren,</option>
                            {Object.entries(targets).map(([key, label]) => (
                              <option key={key} value={key}>
                                {label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 pr-4">
                          <ConfidenceBadge conf={conf} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Vorschau-Tabelle */}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.18em] text-[#7A827C] mb-2 flex items-center gap-2">
                <TableIcon size={14} /> Vorschau ({activeSheet.total_rows} Zeilen gesamt)
              </div>
              <div className="overflow-x-auto rounded-2xl bg-bone/40 border border-bone-200">
                <table className="w-full text-xs">
                  <thead className="bg-bone/60">
                    <tr>
                      {activeSheet.headers.map((h, i) => (
                        <th key={i} className="text-left px-3 py-2 font-semibold text-forest-900">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {activeSheet.sample_rows.map((row, i) => (
                      <tr key={i} className="border-t border-bone-200">
                        {row.map((cell, j) => (
                          <td key={j} className="px-3 py-2 text-[#4A524C]">
                            {cell || <span className="text-[#9AA399]">,</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mt-7 flex flex-wrap gap-3 justify-between items-center">
              <button
                onClick={reset}
                className="text-sm text-[#4A524C] hover:underline"
                data-testid="import-reset"
              >
                ← Andere Datei wählen
              </button>
              <button
                onClick={startCommit}
                disabled={busy}
                data-testid="import-commit-btn"
                className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 disabled:bg-forest-600/40 text-white font-medium text-sm px-6 py-3 rounded-full shadow-sm hover:shadow-md transition-all"
              >
                {busy ? (
                  <>
                    <Spinner size={16} className="animate-spin" /> Importiere…
                  </>
                ) : (
                  <>
                    {activeSheet.total_rows} Zeilen importieren <ArrowRight size={16} weight="bold" />
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      )}

      {step === 3 && commitResult && (
        <section className="bg-white rounded-3xl border border-bone-200 p-6 sm:p-10" data-testid="import-step-result">
          <div className="text-center max-w-lg mx-auto">
            <div className="mx-auto w-16 h-16 rounded-2xl bg-forest-50 text-forest-700 flex items-center justify-center mb-4">
              <CheckCircle size={32} weight="fill" className="text-forest-600" />
            </div>
            <h2 className="font-heading text-2xl font-semibold text-forest-900">Import abgeschlossen</h2>
            <p className="text-sm text-[#4A524C] mt-1">
              {commitResult.inserted} {commitResult.data_type === "fields" ? "Flächen" : "Anwendungen"} angelegt.
            </p>

            <div className="mt-6 grid grid-cols-3 gap-3 text-sm">
              <Stat label="Importiert" value={commitResult.inserted} color="green" />
              <Stat label="Übersprungen" value={commitResult.skipped?.length || 0} color="amber" />
              <Stat label="Fehler" value={commitResult.errors?.length || 0} color="red" />
            </div>

            {(commitResult.skipped?.length > 0 || commitResult.errors?.length > 0) && (
              <details className="mt-6 text-left bg-bone/40 rounded-2xl p-4">
                <summary className="cursor-pointer text-sm font-medium text-forest-700">Details anzeigen</summary>
                <div className="mt-3 max-h-72 overflow-y-auto text-xs space-y-2">
                  {commitResult.skipped?.map((s, i) => (
                    <div key={`s${i}`} className="flex items-start gap-2 text-amber-900">
                      <Warning size={14} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                      <span>Zeile {s.row}: {s.reason}</span>
                    </div>
                  ))}
                  {commitResult.errors?.map((e, i) => (
                    <div key={`e${i}`} className="flex items-start gap-2 text-red-900">
                      <X size={14} weight="bold" className="text-red-600 mt-0.5 shrink-0" />
                      <span>Zeile {e.row}: {e.reason}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}

            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <button
                onClick={reset}
                className="inline-flex items-center gap-2 border border-forest-600/30 text-forest-700 hover:bg-forest-50 font-medium text-sm px-5 py-2.5 rounded-full"
                data-testid="import-again"
              >
                Weitere Datei importieren
              </button>
              <button
                onClick={() =>
                  navigate(commitResult.data_type === "fields" ? "/app/fields" : "/app/applications")
                }
                className="inline-flex items-center gap-2 bg-forest-600 hover:bg-forest-700 text-white font-medium text-sm px-5 py-2.5 rounded-full"
                data-testid="import-go-to-data"
              >
                Zu meinen {commitResult.data_type === "fields" ? "Flächen" : "Anwendungen"}{" "}
                <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

const Stepper = ({ step }) => (
  <ol className="flex items-center gap-4 text-xs uppercase tracking-[0.16em]" data-testid="import-stepper">
    {["Datei wählen", "Spalten zuordnen", "Importieren"].map((label, idx) => {
      const n = idx + 1;
      const active = step === n;
      const done = step > n;
      return (
        <li key={idx} className="flex items-center gap-2">
          <span
            className={`w-7 h-7 rounded-full flex items-center justify-center font-semibold ${
              done
                ? "bg-forest-600 text-white"
                : active
                ? "bg-forest-900 text-white"
                : "bg-bone border border-bone-200 text-[#7A827C]"
            }`}
          >
            {done ? <CheckCircle size={14} weight="fill" /> : n}
          </span>
          <span className={active ? "text-forest-900 font-semibold" : "text-[#7A827C]"}>{label}</span>
          {n < 3 && <span className="w-6 h-px bg-bone-200" />}
        </li>
      );
    })}
  </ol>
);

const ConfidenceBadge = ({ conf }) => {
  const tone =
    conf >= 80 ? "bg-emerald-50 text-emerald-800" : conf >= 50 ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-800";
  return <span className={`inline-flex items-center text-xs font-semibold px-2 py-0.5 rounded-full ${tone}`}>{conf}%</span>;
};

const Stat = ({ label, value, color }) => {
  const tone =
    color === "green"
      ? "bg-emerald-50 text-emerald-900 border-emerald-200"
      : color === "amber"
      ? "bg-amber-50 text-amber-900 border-amber-200"
      : "bg-red-50 text-red-900 border-red-200";
  return (
    <div className={`rounded-2xl border p-4 ${tone}`}>
      <div className="text-3xl font-heading font-semibold">{value}</div>
      <div className="text-xs uppercase tracking-[0.18em] mt-1 opacity-80">{label}</div>
    </div>
  );
};
